import { ApiClient } from '@/shared/api-client';
import { IActivity } from '@/shared/api-client/models/activity';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ArgumentValidator, ConsoleFacade, NameOf, TextareaInputResizer } from '@/shared/helpers';
import { IMotionSchema } from '@/shared/api-client/models/motion/motion-schema';
import { UIEventHandler } from '@/shared/helpers/ui-event-handler';
import { IValidationErrorViewModel } from '@/public/shared/models/validation-error-viewModel';
import { INewMotion } from '@/shared/api-client/models/motion/new-motion';
import { IHierarchyNode } from '@/shared/api-client/models/hierarchy-node';
import { IMotionCategory } from '@/shared/api-client/models/motion/motion-category';
import HierarchyDropdownComponent from '@/shared/components/hierarchy-dropdown-component/hierarchy-dropdown-component.vue';
import { HierarchyFinder } from '@/shared/helpers/hierarchy-finder';
import { StringComparer } from '@/shared/helpers/string-comparer';
import { MotionAuthorType } from '@/shared/api-client/enums/motion-author-type';

interface IViewModel {
    activity?: IActivity;
    schema?: IMotionSchema;
    motionCategories?: IHierarchyNode<IMotionCategory>[];
    isInitialized: boolean;
    isStoring: boolean;
    viewState: 'MotionForm' | 'SuccessMessage';
    errors?: IValidationErrorViewModel[];
    isValidMotionCategory?: boolean;
}

interface IFormModel {
    motionCategoryId: (string | undefined),
    properties: { [id: string]: (string | string[]); }
}

interface IPredictedAuthor {
    authorType: MotionAuthorType;
    targetOrganizationalUnitPath: string;
}

@Component({
    components: {
        HierarchyDropdownComponent
    }
})
export default class ActivityMotioningComponent extends Vue {

    @Prop() public id!: string;
    @Prop({ default: 'sv-SE' }) public cultureAlias!: string;
    @Prop() public accessTicketId!: string;
    @Prop() public predictedAuthor!: IPredictedAuthor;
    @Prop() public translations!: { [key: string]: string; };

    public formData: IFormModel = {
        motionCategoryId: undefined,
        properties: {}
    };

    public viewModel: IViewModel = {
        isInitialized: false,
        isStoring: false,
        viewState: 'MotionForm'
    };

    private apiClient: ApiClient = new ApiClient();

    public async created() {

        await this.initialize();
    }

    public get isInProgress(): boolean {

        return this.viewModel.activity?.status === 'InProgress';
    }

    public get isNotStarted(): boolean {

        return this.viewModel.activity?.status === 'NotStarted';
    }

    public get isFinished(): boolean {

        return this.viewModel.activity?.status === 'Finished'
    }

    public get motionCategoriesExists(): boolean {
        return this.viewModel.motionCategories !== undefined && this.viewModel.motionCategories.length > 0;
    }

    public get currentMotionCategory(): (IHierarchyNode<IMotionCategory> | undefined) {

        if (this.formData?.motionCategoryId == null) {
            return;
        }

        if (this.viewModel.motionCategories == null) {
            return;
        }

        return HierarchyFinder.find(this.viewModel.motionCategories, x => StringComparer.compare(x.entity.id, this.formData.motionCategoryId));
    }

    public get motionCategoryLabel(): string {

        const categoryProperty = this.viewModel.schema?.properties.find(property => property.identifier.toLowerCase() === 'ev_category' && property.type === 'Custom');

        if (categoryProperty) {

            return categoryProperty.name;
        }

        return '';
    }

    public async addProposal(propertyIdentifier: string, $event?: UIEvent): Promise<void> {

        ArgumentValidator.notNullOrEmpty(propertyIdentifier, `propertyIdentifier`);
        UIEventHandler.preventDefaults($event);

        let newValue: string[] = [];

        if (this.formData.properties[propertyIdentifier]) {
            newValue = this.formData.properties[propertyIdentifier] as string[];
        }

        newValue.push('');

        this.$set(this.formData.properties, propertyIdentifier, newValue);
    }

    public async removeProposal(propertyIdentifier: string, index: number, $event?: UIEvent): Promise<void> {

        ArgumentValidator.notNullOrEmpty(propertyIdentifier, `propertyIdentifier`);
        ArgumentValidator.notLessThanZero(index, `index`);
        UIEventHandler.preventDefaults($event);

        const newValue: string[] = this.formData.properties[propertyIdentifier] as string[];

        if (newValue?.length <= 1) {
            return;
        }

        newValue.splice(index, 1);

        this.$set(this.formData.properties, propertyIdentifier, newValue);
    }

    public isRemovableProposal(propertyIdentifier: string): boolean {

        ArgumentValidator.notNullOrEmpty(propertyIdentifier, `propertyIdentifier`);

        return this.formData.properties[propertyIdentifier]?.length > 1;
    }

    public async checkHeight($event: InputEvent): Promise<void> {

        TextareaInputResizer.checkHeight($event);
    }

    public onSelectedMotionCategoryChanged(node: IHierarchyNode<IMotionCategory>): void {

        const selectedNode = node || undefined;

        if (!selectedNode) {
            this.viewModel.isValidMotionCategory = undefined;
        } else {
            this.viewModel.isValidMotionCategory = (selectedNode.nodes?.length || 0) === 0;
        }

        this.$set(this.formData, NameOf.property(this.formData, x => x.motionCategoryId), selectedNode.entity?.id);
    }

    public async submit($event: UIEvent) {

        $event.preventDefault();

        if (this.viewModel.isStoring) {

            return;
        }

        this.validate();

        if (this.viewModel.errors !== null && (this.viewModel.errors?.length || 0) >= 1) {
            return;
        }

        this.viewModel.isStoring = true;

        const newMotion: INewMotion = {
            motionCategoryId: this.formData.motionCategoryId,
            propertyValues: []
        };

        this.viewModel.schema?.properties.forEach((p) => {

            if (p.type === 'Proposals') {

                newMotion.propertyValues.push({
                    identifier: p.identifier,
                    valueProposalList: this.formData.properties[p.identifier] as string[]
                });

            } else {

                newMotion.propertyValues.push({
                    identifier: p.identifier,
                    valueString: this.formData.properties[p.identifier] as string
                });
            }
        });

        try {

            await this.apiClient.motion.create(this.id, newMotion, this.accessTicketId);

            this.viewModel.isStoring = false;

            this.viewModel.viewState = 'SuccessMessage';

        } catch (error: any) {

            this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.errors), [{
                name: this.translations[`validation_error_general_header`] || '',
                message: this.translations[`validation_error_general_message`] || ''
            }]);

            this.viewModel.isStoring = false;
        }
    }

    private validate(): void {

        const errors: IValidationErrorViewModel[] = [];

        const hasCategories = (this.viewModel.motionCategories && this.viewModel.motionCategories.length > 0);

        if (hasCategories && this.viewModel.isValidMotionCategory !== true) {
            errors.push({
                formControlId: 'motionCategory',
                name: 'Kategori',
                message: this.translations?.validation_isRequired
            });
        }

        this.viewModel.schema?.properties?.forEach(p => {

            if (p.isRequired) {

                const value = this.formData.properties[p.identifier];

                if (p.type === 'SingleLineText' || p.type === 'MultiLineText') {

                    if (!value || (value as string) === ``) {
                        errors.push({
                            formControlId: p.identifier,
                            name: p.name,
                            message: this.translations?.validation_isRequired
                        });
                    }
                }

                if (p.type === 'Proposals') {

                    if (!value || (value as string[]).length <= 0 || (value as string[]).some(v => v === '')) {
                        errors.push({
                            formControlId: p.identifier,
                            name: p.name,
                            message: this.translations?.validation_isRequired
                        });
                    }
                }
            }
        });

        this.$set(this.viewModel, NameOf.property(this.viewModel, x => x.errors), errors);
    }

    private async initialize() {

        try {

            this.viewModel.activity = await this.apiClient.activity.get(this.id, this.accessTicketId);

            this.viewModel.schema = await this.apiClient.motion.schema(this.viewModel.activity.id, this.cultureAlias);

            this.viewModel.schema.properties
                .filter(x => x.type === 'Proposals')
                .forEach((p) => {
                    this.addProposal(p.identifier);
                });

            this.viewModel.motionCategories = await this.apiClient.motionCategory.getHierarchy(this.viewModel.activity.id, this.cultureAlias);

        } catch (error: any) {

            ConsoleFacade.log(`ActivityComponent: An error occurred`, error);
        }

        this.viewModel.isInitialized = true;
    }
}
